/**
 * Email Communications Hook
 * Manages email queue and template data fetching and mutations
 */

import { useState, useCallback } from 'react';
import { useEmailService, useTemplateService } from '@actschurch/ccms-sdk';
import type { AppEmail } from '@actschurch/ccms-sdk';
import type { AppEmailTemplateModelView } from '@actschurch/ccms-sdk';
import { toast } from '../../../../../components/ui/providers/AlertProvider';
import type { EmailApiResponse } from '../types';

export function useEmailCommunications() {
  const emailService = useEmailService();
  const templateService = useTemplateService();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queueItems, setQueueItems] = useState<EmailApiResponse[]>([]);
  const [templates, setTemplates] = useState<AppEmailTemplateModelView[]>([]);

  // ==================== QUEUE OPERATIONS ====================

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The API returns EmailApiResponse format
      const data = await emailService.getAll() as unknown as EmailApiResponse[];
      setQueueItems(data);
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to fetch email queue';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [emailService]);

  const fetchQueueItem = useCallback(
    async (id: number): Promise<EmailApiResponse> => {
      try {
        // The API returns EmailApiResponse format
        return await emailService.getById(id) as unknown as EmailApiResponse;
      } catch (err: any) {
        const errorMessage = err?.message || `Failed to fetch email ${id}`;
        setError(errorMessage);
        throw err;
      }
    },
    [emailService]
  );

  const deleteQueueItem = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        await toast.promise(emailService.delete(id), {
          loading: 'Deleting email...',
          success: 'Email deleted successfully',
          error: (err) => `Failed to delete: ${err.message}`,
        });
        await fetchQueue();
      } catch (err: any) {
        setError(err?.message || 'Failed to delete email');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [emailService, fetchQueue]
  );

  const retryFailedItem = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        // Get the current item using emailID
        const item = queueItems.find((q) => q.emailID === id);
        if (!item) throw new Error('Email not found');

        // Reset status to Pending
        await emailService.update(id, {
          ...item,
          status: 'Pending',
        } as any);

        await fetchQueue();
        toast.success('Email queued for retry');
      } catch (err: any) {
        setError(err?.message || 'Failed to retry email');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [emailService, fetchQueue, queueItems]
  );

  const updatePriority = useCallback(
    async (id: number, priority: number) => {
      setLoading(true);
      try {
        // Use emailID for lookups
        const item = queueItems.find((q) => q.emailID === id);
        if (!item) throw new Error('Email not found');

        await toast.promise(
          emailService.update(id, { ...item, priority } as any),
          {
            loading: 'Updating priority...',
            success: 'Priority updated successfully',
            error: (err) => `Failed to update priority: ${err.message}`,
          }
        );

        await fetchQueue();
      } catch (err: any) {
        setError(err?.message || 'Failed to update priority');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [emailService, fetchQueue, queueItems]
  );

  const updateQueueItem = useCallback(
    async (id: number, data: Partial<AppEmail>) => {
      setLoading(true);
      try {
        await toast.promise(emailService.update(id, data), {
          loading: 'Updating email...',
          success: 'Email updated successfully',
          error: (err) => `Failed to update: ${err.message}`,
        });

        await fetchQueue();
      } catch (err: any) {
        setError(err?.message || 'Failed to update email');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [emailService, fetchQueue]
  );

  const createEmail = useCallback(
    async (data: Partial<AppEmail>) => {
      setLoading(true);
      try {
        await toast.promise(emailService.create(data), {
          loading: 'Queuing email...',
          success: 'Email queued successfully',
          error: (err) => `Failed to queue email: ${err.message}`,
        });

        await fetchQueue();
      } catch (err: any) {
        setError(err?.message || 'Failed to create email');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [emailService, fetchQueue]
  );

  // ==================== TEMPLATE OPERATIONS ====================

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await templateService.getEmailTemplates();
      setTemplates(data);
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to fetch email templates';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [templateService]);

  const fetchTemplate = useCallback(
    async (id: number): Promise<AppEmailTemplateModelView> => {
      try {
        return await templateService.getEmailTemplate(id);
      } catch (err: any) {
        const errorMessage = err?.message || `Failed to fetch template ${id}`;
        setError(errorMessage);
        throw err;
      }
    },
    [templateService]
  );

  const createTemplate = useCallback(
    async (data: any) => {
      setLoading(true);
      try {
        await toast.promise(templateService.createEmailTemplate(data), {
          loading: 'Creating template...',
          success: 'Template created successfully',
          error: (err) => `Failed to create template: ${err.message}`,
        });

        await fetchTemplates();
      } catch (err: any) {
        setError(err?.message || 'Failed to create template');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [templateService, fetchTemplates]
  );

  const updateTemplate = useCallback(
    async (id: number, data: any) => {
      setLoading(true);
      try {
        await toast.promise(templateService.updateEmailTemplate(id, data), {
          loading: 'Updating template...',
          success: 'Template updated successfully',
          error: (err) => `Failed to update template: ${err.message}`,
        });

        await fetchTemplates();
      } catch (err: any) {
        setError(err?.message || 'Failed to update template');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [templateService, fetchTemplates]
  );

  const deleteTemplate = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        await toast.promise(templateService.deleteEmailTemplate(id), {
          loading: 'Deleting template...',
          success: 'Template deleted successfully',
          error: (err) => `Failed to delete template: ${err.message}`,
        });

        await fetchTemplates();
      } catch (err: any) {
        setError(err?.message || 'Failed to delete template');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [templateService, fetchTemplates]
  );

  // ==================== COMBINED FETCH ====================

  const fetchAll = useCallback(async () => {
    await Promise.all([fetchQueue(), fetchTemplates()]);
  }, [fetchQueue, fetchTemplates]);

  return {
    loading,
    error,
    setError,

    // Queue
    queueItems,
    fetchQueue,
    fetchQueueItem,
    deleteQueueItem,
    retryFailedItem,
    updatePriority,
    updateQueueItem,
    createEmail,

    // Templates
    templates,
    fetchTemplates,
    fetchTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,

    // Combined
    fetchAll,
  };
}
